import React, {Fragment, useMemo} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, FormattedNumber} from 'react-intl'
import {Box, Flex, Skeleton, Text, useSlotRecipe, VisuallyHidden} from '@chakra-ui/react3'
import {useCurrency} from '../../../hooks/use-currency'
import {useCurrentCustomer} from '../../../hooks/use-current-customer'
import displayPriceRecipe from './index.recipe'

/**
 * The DisplayPrice is a visual representation of all price settings of products
 * @param {'xs'|'sm'|'md'|object} size - type scale for the price presentation
 * @param {'start'|'center'|'end'} alignment - alignment for the price block
 *
 */
const DisplayPrice = ({
    price,
    displayPrice,
    currency,
    loading,
    isFree,
    hasDiscount,
    hidePromotionSignals,
    showVatLabel,
    size,
    alignment
}) => {
    const {currency: activeCurrency} = useCurrency()
    const {isCustomNetPriceEnabled} = useCurrentCustomer().data

    // when promo signals are hidden, render the calculated price as a regular price
    const showDiscount = hasDiscount && !hidePromotionSignals

    const recipe = useSlotRecipe({recipe: displayPriceRecipe})
    const styles = useMemo(
        () =>
            recipe({
                size,
                alignment,
                showVatLabel,
                isFree,
                hasDiscount: showDiscount,
                isCustomNetPriceEnabled
            }),
        [recipe, size, alignment, showVatLabel, isFree, showDiscount, isCustomNetPriceEnabled]
    )

    const SkeletonComponent = loading ? Skeleton : Fragment
    const skeletonProps = loading ? {loading, as: 'div'} : {}

    return (
        <Box css={styles.container}>
            <SkeletonComponent {...skeletonProps}>
                <Text css={styles.taxesLabel} data-testid="taxes">
                    {!isCustomNetPriceEnabled ? (
                        <>
                            <FormattedMessage
                                id="global.taxes_included"
                                defaultMessage="tax included"
                            />
                        </>
                    ) : (
                        <FormattedMessage
                            id="global.taxes_not_included"
                            defaultMessage="tax not included"
                        />
                    )}
                </Text>
            </SkeletonComponent>
            <Flex css={styles.priceOrder}>
                <SkeletonComponent {...skeletonProps}>
                    <Box css={styles.priceContainer}>
                        {showDiscount && (
                            <VisuallyHidden>
                                <FormattedMessage
                                    defaultMessage="Discounted price: "
                                    id="item_attributes.label.discounted_price"
                                />
                            </VisuallyHidden>
                        )}
                        {isFree ? (
                            <Text css={styles.freeLabel} data-testid="isFree">
                                <FormattedMessage defaultMessage="Free" id="global.free" />
                            </Text>
                        ) : (
                            <Text css={styles.price} data-testid="displayPrice">
                                <FormattedNumber
                                    style="currency"
                                    currency={currency || activeCurrency}
                                    value={displayPrice}
                                />
                            </Text>
                        )}
                    </Box>
                </SkeletonComponent>
                {showDiscount && (
                    <SkeletonComponent {...skeletonProps}>
                        <Text css={styles.originalPrice} data-testid="price">
                            <VisuallyHidden>
                                <FormattedMessage
                                    defaultMessage="Original price: "
                                    id="item_attributes.label.original_price"
                                />
                            </VisuallyHidden>
                            <FormattedNumber
                                style="currency"
                                currency={currency || activeCurrency}
                                value={price}
                            />
                        </Text>
                    </SkeletonComponent>
                )}
            </Flex>
        </Box>
    )
}

DisplayPrice.propTypes = {
    price: PropTypes.number,
    displayPrice: PropTypes.number,
    currency: PropTypes.string,
    loading: PropTypes.bool,
    isFree: PropTypes.bool,
    hasDiscount: PropTypes.bool,
    hidePromotionSignals: PropTypes.bool,
    showVatLabel: PropTypes.bool,
    size: PropTypes.oneOfType([PropTypes.oneOf(['xs', 'sm', 'md']), PropTypes.object]),
    alignment: PropTypes.oneOf(['start', 'center', 'end'])
}

export default DisplayPrice
